import { DataFormatInstrument } from "./DataFormatInstrument";
import { FileExcludePattern } from "./DataSource";
import { Format } from "./Format";
import {
  IGenericRequestParametersTranslator,
  INamedEntityLabels,
  IPaginationParametersTranslator,
  PropertyTranslator,
} from "./GenericTranslator";
import {
  IGenericRequestParameters,
  IEntityMinimalModel,
  IModelWithStringId,
  INamedEntity,
  IPaginationParameters,
  EntityConstants,
  GenericEntityOrderBy,
  NamedEntityOrderBy,
} from "./GenericTypes";
import { Method } from "./Method";
import { Vendor } from "./Vendors";

export const dataFormatsConstants: EntityConstants<DataFormat, DataFormatFilters> = {
  resource: "data_formats",
  frontendIndexRoute: "formats",
  entitySingular: "data format",
  entityPlural: "data formats",
  icon: "code",
};

export const DataFormatFieldLabels: PropertyTranslator<DataFormat> = {
  ...INamedEntityLabels,
  id: "Data format ID",
  version: "Version",
  exec: "Execution command",
  interpreter: "Interpreter",
  keepParentFolders: "Keep parent folders",
  wholeFolder: "Whole folder",
  patterns: "Patterns",
  majorVersion: "Major version",
  minorVersion: "Minor version",
  hasVisualization: "Has visualization",
  isCustom: "Is custom",
  isBeta: "Is beta",
  vendors: "Vendors",
  instruments: "Instruments",
  methods: "Methods",
  formats: "Software",
  fileExcludePatterns: "Excluded files",
} as const;

export interface DataFormatFingerprints {
  [pattern: string]: FileFingerprint;
}
export interface FileFingerprint {
  forced_rules?: {
    force_fingerprint?: boolean;
    force_additional_filepattern?: boolean;
  };
  fingerprints?: Fingerprint[];
}
export interface Fingerprint {
  string: string;
  encoding: "binary" | "ascii";
  type: "magicbyte" | "line";
  count?: number;
  offset?: number;
}
export interface Pattern {
  re: string;
  template: string;
  required?: boolean;
  secondary?: boolean;
  file?: boolean;
  directory?: boolean;
}
export interface DataFormat extends IModelWithStringId, INamedEntity {
  version: string;
  exec: string;
  interpreter: string;
  keepParentFolders: number | null;
  wholeFolder: boolean;
  patterns: Pattern[] | null;
  vendors: Vendor[] | null;
  methods: Method[] | null;
  formats: Format[] | null;
  instruments: DataFormatInstrument[] | null;
  majorVersion: number;
  minorVersion: number;
  fileExcludePatterns?: FileExcludePattern[];
  isCustom: boolean;
  hasVisualization: boolean;
  isBeta: boolean;
}
export interface DataFormatSuggestions extends Omit<IEntityMinimalModel<DataFormat["id"]>, "version"> {}

export interface DataFormatFilters
  extends IGenericRequestParameters<DataFormat, DataFormatSortingOptions>,
    IPaginationParameters {
  vendorIds?: string[] | null;
  methodIds?: string[] | null;
  instrumentIds?: string[] | null;
  formatIds?: string[] | null;
  includeBetaParsers?: boolean | null;
  isBeta?: boolean | null;
  isCustom?: boolean | null;
  hasVisualization?: boolean | null;
}
export const DataFormatFiltersTranslator: PropertyTranslator<DataFormatFilters> = {
  ...IGenericRequestParametersTranslator,
  ...IPaginationParametersTranslator,
  vendorIds: "Vendors",
  methodIds: "Methods",
  instrumentIds: "Instruments",
  formatIds: "Formats",
  includeBetaParsers: "Include parsers in development",
  isBeta: "State",
  isCustom: "Is custom format",
  hasVisualization: "Has visualization",
} as const;

export const DataFormatSortingOptionsConsts = [...GenericEntityOrderBy, ...NamedEntityOrderBy] as const;
export type DataFormatSortingOptions = (typeof DataFormatSortingOptionsConsts)[number];
export interface Parsers {
  results: DataFormat[];
}

export interface Parsers {
  results: DataFormat[];
}

// Pattern Matching
export interface FormatFileEntryResponse extends FormatFileEntryRequest {
  isUnique: boolean;
  fragments?: FingerprintFragment[];
}
export interface FormatFileEntryRequest {
  // name: string;
  id: string;
  fullPath: string;
  fragments?: FingerprintFragment[];
}
export interface MatchRequest {
  files: FormatFileEntryRequest[];
  formatIds?: string[];
}

export interface MatchedDataset {
  id: string;
  parserId: string;
  name: string;
  parentMissing: boolean;
  files: FormatFileEntryResponse[];
  parentPath: string;
}

export interface ParserMatchResult {
  parserId: string;
  parserName: string;
  datasets: MatchedDataset[];
}

export interface FingerprintFragment {
  offset: number;
  length: number;
  bytes: string | null;
}
export interface FileFragment {
  id: string;
  fragments: FingerprintFragment[];
}
export interface MatchResult {
  matches: ParserMatchResult[];
  missingFragments: FileFragment[];
}

export type ParserDevelopmentState = "Unknown" | "ProductionOnly" | "DevelopmentOnly" | "ProductionAndDevelopment";

export interface ParserSelection {
  id: string;
  name: string;
  isDevelopmentVersion: boolean;
  developmentState: ParserDevelopmentState;
}
